import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { GetTicketCommits, GetCommitFiles } from "../../global/Apis";
import { msalInstance } from "../../global/Apis";
import { ReleaseVersionIssue, TicketCommitsResponse, TicketCommit } from "../../global/Types";

interface ReleaseVersionDetailsProps {
  issue: ReleaseVersionIssue | null;
  isOpen: boolean;
  onClose: () => void;
}

const groupByRepo = (commits: TicketCommit[]) => {
  const grouped: { [repo: string]: TicketCommit[] } = {};
  commits.forEach(commit => {
    if (!grouped[commit.repo_name]) grouped[commit.repo_name] = [];
    grouped[commit.repo_name].push(commit);
  });
  return grouped;
};

const ReleaseVersionDetails: React.FC<ReleaseVersionDetailsProps> = ({ issue, isOpen, onClose }) => {
  const { accounts } = useMsal();
  const account = accounts[0];
  const [commitsData, setCommitsData] = useState<TicketCommitsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRepo, setExpandedRepo] = useState<string | null>(null);
  const [expandedCommit, setExpandedCommit] = useState<string | null>(null);
  const [commitFiles, setCommitFiles] = useState<any>(null);
  const [commitFilesLoading, setCommitFilesLoading] = useState(false);
  const [commitFilesError, setCommitFilesError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && issue) {
      setLoading(true);
      setError(null);
      setCommitsData(null);
      setExpandedRepo(null);
      setExpandedCommit(null);
      setCommitFiles(null);
      setCommitFilesError(null);
      GetTicketCommits(issue.ticket_number, msalInstance, account)
        .then(setCommitsData)
        .catch(() => setError("Failed to load commits."))
        .finally(() => setLoading(false));
    }
  }, [isOpen, issue, account]);

  const handleViewClick = async (repo: string, sha: string) => {
    if (expandedCommit === sha) {
      setExpandedCommit(null);
      setCommitFiles(null);
      setCommitFilesError(null);
      return;
    }
    setExpandedCommit(sha);
    setCommitFilesLoading(true);
    setCommitFilesError(null);
    try {
      const files = await GetCommitFiles(repo, sha, msalInstance, account);
      setCommitFiles(files);
    } catch {
      setCommitFilesError("Failed to load commit files.");
      setCommitFiles(null);
    } finally {
      setCommitFilesLoading(false);
    }
  };

  if (!isOpen || !issue) return null;

  // build a set of branch-only commit shas for quick lookup
  const branchOnlyShas = new Set(
    (commitsData?.commits ?? [])
        .filter(c => c.jira_ticket_source === "branch")
        .map(c => c.commit_sha)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full p-8 relative border border-indigo-100 max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-4 right-4 text-indigo-400 hover:text-indigo-700 text-xl font-bold focus:outline-none"
          onClick={onClose}
          aria-label="Close"
        >
          &#10005;
        </button>

        {/* Header */}
        <div className="border-b border-indigo-100 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <a
              href={issue.issue_url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="text-2xl font-bold text-indigo-700 hover:underline"
            >
              {issue.ticket_number}
            </a>
            {issue.branch_only_commits?.length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                <svg viewBox="0 0 16 16" fill="none" className="w-3 h-3 flex-shrink-0">
                  <path d="M8 2L14.5 13.5H1.5L8 2Z" fill="#D97706"/>
                  <rect x="7.3" y="6" width="1.4" height="4" rx="0.7" fill="white"/>
                  <rect x="7.3" y="10.8" width="1.4" height="1.4" rx="0.7" fill="white"/>
                </svg>
                {issue.branch_only_commits.length} {issue.branch_only_commits.length === 1 ? "commit" : "commits"} missing ticket ref
              </span>
            )}
          </div>
          <p className="text-gray-600 text-sm mt-1">{issue.summary}</p>
        </div>

        {/* Commits section */}
        <div>
          {loading && <div className="text-gray-500 py-8 text-center">Loading commits...</div>}
          {error && <div className="text-red-500">{error}</div>}

          {/* Empty state */}
          {commitsData && commitsData.commits.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <svg className="w-10 h-10 text-gray-300 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p className="text-gray-500 font-medium">No commits found for this ticket</p>
              <p className="text-gray-400 text-xs mt-1">No commits in the database reference {issue.ticket_number}</p>
            </div>
          )}

          {/* Commits grouped by repo */}
          {commitsData && commitsData.commits.length > 0 && (
            <div className="space-y-2">
              {Object.entries(groupByRepo(commitsData.commits)).map(([repo, commits]) => (
                <div key={repo} className="mb-2 border rounded-lg border-indigo-100 bg-indigo-50">
                  <button
                    className="w-full flex justify-between items-center px-4 py-2 font-semibold text-indigo-700 focus:outline-none hover:bg-indigo-100 transition"
                    onClick={() => setExpandedRepo(expandedRepo === repo ? null : repo)}
                  >
                    <span>{repo}</span>
                    <span className="text-xs text-indigo-500">{commits.length} commit{commits.length !== 1 ? "s" : ""}</span>
                    <span className="ml-2">{expandedRepo === repo ? "▲" : "▼"}</span>
                  </button>
                  {expandedRepo === repo && (
                    <div className="bg-white border-t border-indigo-100">
                      {commits.map((commit, idx) => {
                        const isBranchOnly = branchOnlyShas.has(commit.commit_sha);
                        return (
                          <React.Fragment key={commit.commit_sha + idx}>
                            <div
                              className={`px-4 py-2 text-sm border-b border-indigo-50 grid grid-cols-[56px_1fr_auto] gap-2 items-start ${
                                isBranchOnly ? "bg-amber-50 border-l-2 border-l-amber-400" : ""
                              }`}
                            >
                              <span className={`font-mono text-xs pt-0.5 ${isBranchOnly ? "text-amber-700" : "text-indigo-700"}`}>
                                 <a href={commit.commit_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`font-mono text-xs pt-0.5 hover:underline ${isBranchOnly ? "text-amber-700" : "text-indigo-700"}`}
                                >
                                  {commit.short_sha}
                                </a>
                              </span>
                              <div className="flex flex-col gap-1">
                                <span className="text-gray-800 whitespace-normal break-words">{commit.message}</span>
                                <div className="flex gap-2 text-xs text-gray-400">
                                  <span>{commit.author}</span>
                                  <span>{new Date(commit.committed_at).toLocaleString()}</span>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1 pt-0.5">
                                {isBranchOnly && (
                                  <span className="text-xs font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                                    no ticket ref
                                  </span>
                                )}
                                <button
                                  className="text-indigo-500 underline text-xs font-semibold focus:outline-none"
                                  onClick={() => handleViewClick(commit.repo_name, commit.commit_sha)}
                                >
                                  {expandedCommit === commit.commit_sha ? "Hide" : "View"}
                                </button>
                              </div>
                            </div>
                            {expandedCommit === commit.commit_sha && (
                              <div className="bg-gray-50 border-t border-indigo-100 px-4 py-3">
                                {commitFilesLoading && <div className="text-gray-500">Loading files...</div>}
                                {commitFilesError && <div className="text-red-500">{commitFilesError}</div>}
                                {commitFiles && commitFiles.files && commitFiles.files.length > 0 && (
                                  <div className="flex flex-col space-y-2">
                                    {commitFiles.files.map((file: any, i: number) => (
                                      <div key={file.filename + i} className="flex items-center space-x-3 text-xs">
                                        <span className="bg-green-100 text-green-700 font-bold px-2 py-1 rounded min-w-[32px] text-center">+{file.additions}</span>
                                        <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded min-w-[32px] text-center">-{file.deletions}</span>
                                        <span className="bg-amber-100 text-amber-700 font-bold px-2 py-1 rounded min-w-[32px] text-center">±{file.changes}</span>
                                        <span className="truncate font-mono text-indigo-800">{file.filename}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {commitFiles && commitFiles.files && commitFiles.files.length === 0 && (
                                  <div className="text-gray-500">No files changed in this commit.</div>
                                )}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReleaseVersionDetails;